import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios'
import { useAgeGateStore } from '../src/ageGate/stores/ageGateStore'
import { AGE_GATE_API_CODES } from '../src/ageGate/constants/ageGate.constants'
import { useSupabaseAuth } from '../src/shared/contexts/SupabaseAuthContext'
import { useCallback, useRef, useMemo, useEffect } from 'react'
import { router } from 'expo-router'
import { API_URL } from '../constants/api'
import { isApiDebugEnabled } from '../constants/debug'
import { logApiRequest, logApiResponse, logApiError } from './railwayDebug.helper'
import { logger } from '../src/helpers/logger.helper'
import { getImpersonationToken } from '../src/zportiAdmin/helpers/impersonationToken.helper'
import { markNetworkReachable, markNetworkUnreachable } from '../src/shared/helpers/networkStatus.helper'
import { useDialogStore } from '../src/shared/stores/dialogStore'
import { useSnackbarStore } from '../src/shared/stores/snackbarStore'
import { buildPublicSignInHref } from '../src/signIn/helpers/postAuthRedirect.helper'

/**
 * Transient responseless network failures (AxiosError "Network Error", status 0) are common
 * on mobile: a stale keep-alive socket dropped by the server/LB fails the first request fast,
 * and OkHttp will not auto-retry a request whose body was already written (e.g. a PUT). We
 * retry idempotent requests on a fresh connection before surfacing an error or flipping the
 * offline banner. Confirmed (adb logcat) as the root cause of both the false "offline" banner
 * on the Friends tab (the matches fetch) and the "avatar update fails first try" report.
 *
 * POST is excluded by default — retrying it could double-submit a non-idempotent action
 * (e.g. a bet or challenge). The allowlist below carries the exceptions: reads that use POST
 * only to avoid URL-length limits (e.g. /matches/bulk-matches, which the server caches), so
 * retrying them is safe. Match on the path only (query/baseURL stripped).
 */
const NETWORK_RETRY_BACKOFF_MS = [300, 800] as const
const NETWORK_RETRYABLE_METHODS = new Set(['get', 'head', 'put', 'delete', 'options'])
const NETWORK_RETRYABLE_POST_PATHS = new Set(['/matches/bulk-matches'])

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

/** True when a transient network failure on this request is safe to retry (idempotent). */
function isNetworkRetryable(method: string, url: string): boolean {
  const m = method.toLowerCase()
  if (NETWORK_RETRYABLE_METHODS.has(m)) return true
  if (m === 'post') return NETWORK_RETRYABLE_POST_PATHS.has(url.split('?')[0])
  return false
}

/**
 * Custom hook to create an authenticated API client
 * Automatically includes Supabase JWT token in requests
 * Uses axios with interceptors for token management and 401 retry
 */
export function useApiClient() {
  const { getToken, refreshAccessToken, signOut, isLoaded, isSignedIn, userId, isAnonymous } =
    useSupabaseAuth()
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken
  const refreshAccessTokenRef = useRef(refreshAccessToken)
  refreshAccessTokenRef.current = refreshAccessToken
  const signOutRef = useRef(signOut)
  signOutRef.current = signOut

  const isLoadedRef = useRef(isLoaded)
  const isSignedInRef = useRef(isSignedIn)
  const isAnonymousRef = useRef(isAnonymous)
  isLoadedRef.current = isLoaded
  isSignedInRef.current = isSignedIn
  isAnonymousRef.current = isAnonymous

  const authIdentity = isSignedIn ? `${userId ?? 'unknown'}:${isAnonymous ? 'anon' : 'real'}` : 'signed-out'
  const authIdentityRef = useRef(authIdentity)
  authIdentityRef.current = authIdentity

  const tokenCacheRef = useRef<{ token: string | null; timestamp: number; authIdentity: string }>({
    token: null,
    timestamp: 0,
    authIdentity,
  })
  const refreshingTokenPromiseRef = useRef<Promise<string | null> | null>(null)
  const authDeathHandledRef = useRef(false)
  const TOKEN_CACHE_TTL = 5 * 60 * 1000

  useEffect(() => {
    authDeathHandledRef.current = false
    // Never reuse cached tokens after auth identity changes (e.g. real -> anonymous).
    if (tokenCacheRef.current.authIdentity !== authIdentity) {
      tokenCacheRef.current = {
        token: null,
        timestamp: 0,
        authIdentity,
      }
      refreshingTokenPromiseRef.current = null
    }
  }, [authIdentity])

  const instance: AxiosInstance = useMemo(() => {
    const axiosInstance = axios.create({
      baseURL: API_URL,
      headers: { 'Content-Type': 'application/json' },
    })

    axiosInstance.interceptors.request.use(
      async (config) => {
        const now = Date.now()
        const currentAuthIdentity = authIdentityRef.current
        if (tokenCacheRef.current.authIdentity !== currentAuthIdentity) {
          tokenCacheRef.current = {
            token: null,
            timestamp: 0,
            authIdentity: currentAuthIdentity,
          }
        }

        let token = tokenCacheRef.current.token

        if (!token || now - tokenCacheRef.current.timestamp > TOKEN_CACHE_TTL) {
          try {
          token = await getTokenRef.current()
          tokenCacheRef.current = { token, timestamp: now, authIdentity: currentAuthIdentity }
          } catch (tokenError) {
            logger.error('[apiClient] Failed to get token', {
              endpoint: config.url,
              error: tokenError instanceof Error ? tokenError.message : String(tokenError),
            })
            tokenCacheRef.current = { token: null, timestamp: 0, authIdentity: currentAuthIdentity }
            token = null
          }
        }

        const MAX_RETRIES = 10;
        const RETRY_DELAY_MS = 100;
        let retryCount = 0;
        let currentIsLoaded = isLoadedRef.current;
        let currentIsSignedIn = isSignedInRef.current;

        while (!currentIsLoaded && retryCount < MAX_RETRIES) {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
          retryCount++;
          currentIsLoaded = isLoadedRef.current;
          currentIsSignedIn = isSignedInRef.current;
        }

        if (!currentIsLoaded) {
          logger.error('[apiClient] Auth failed to initialize in time', {
            endpoint: config.url,
            retryCount,
          });
          const error = new Error('AUTH_TIMEOUT') as Error & { endpoint?: string };
          error.endpoint = config.url;
          return Promise.reject(error);
        }

        if (!currentIsSignedIn) {
          logger.warn('[apiClient] No active session, rejecting request', {
            endpoint: config.url,
          });
          const error = new Error('No active session') as Error & { endpoint?: string };
          error.endpoint = config.url;
          return Promise.reject(error);
        }

        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        } else {
          logger.error('[apiClient] No token available after checks', {
            endpoint: config.url,
          });
          const error = new Error('Authentication token not available') as Error & { endpoint?: string };
          error.endpoint = config.url;
          return Promise.reject(error);
        }

        // Inject impersonation header if active (memory-only token)
        const impToken = getImpersonationToken()
        if (impToken) {
          config.headers['X-Impersonation-Token'] = impToken
        }

        if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
          delete (config.headers as Record<string, unknown>)['Content-Type']
        }

        if (isApiDebugEnabled) {
          let debugData: unknown = config.data
          const path = config.url || ''
          if (
            path.includes('/users/me/redeem-coupon') &&
            debugData &&
            typeof debugData === 'object' &&
            !Array.isArray(debugData) &&
            'code' in debugData
          ) {
            debugData = { ...(debugData as Record<string, unknown>), code: '[REDACTED]' }
          }
          logApiRequest(path, {
            method: config.method?.toUpperCase(),
            headers: config.headers as Record<string, unknown>,
            data: debugData,
          })
        }

        return config
      },
      (error) => {
        if (isApiDebugEnabled) {
          logApiError('', error, 'Request interceptor error')
        }
        return Promise.reject(error)
      }
    )

    axiosInstance.interceptors.response.use(
      (response) => {
        if (isApiDebugEnabled) {
          logger.debug('🔍 [apiClient] Response received:', {
            endpoint: response.config.url,
            status: response.status,
            statusText: response.statusText,
            ok: response.status >= 200 && response.status < 300,
          })
          logApiResponse(response.config.url || '', response as unknown as Response, response.data)
        }
        markNetworkReachable()
        return response
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean }

        if (error.response?.status === 429) {
          const data = error.response.data
          let message = 'Too many requests, please try again later.'
          if (typeof data === 'string' && data.trim()) {
            message = data.trim()
          } else if (data && typeof data === 'object' && 'message' in data) {
            const msg = (data as { message: unknown }).message
            if (typeof msg === 'string' && msg.trim()) message = msg.trim()
          }
          useSnackbarStore.getState().showSnackbar(message, 'error')
          ;(error as AxiosError & { rateLimitHandled: boolean }).rateLimitHandled = true
          return Promise.reject(error)
        }

        // Anonymous user hitting a real-user-only endpoint — show sign-up prompt.
        // WC pick saves render their own themed prompt (GuestSignUpAfterPickPrompt
        // via PostFirstPickPromptManager); skip the generic dialog there to avoid
        // double-prompting.
        if (error.response?.status === 403) {
          const responseData = error.response.data as { code?: string } | undefined
          // The server is the authority on age: if any call says this account is under 18 or
          // unverified, show the gate even if the startup status check failed or is stale.
          if (responseData?.code === AGE_GATE_API_CODES.INELIGIBLE) {
            const ageGate = useAgeGateStore.getState()
            ageGate.setStatus('blocked', ageGate.userId)
          } else if (
            responseData?.code === AGE_GATE_API_CODES.VERIFICATION_REQUIRED &&
            useAgeGateStore.getState().status !== 'blocked'
          ) {
            const ageGate = useAgeGateStore.getState()
            ageGate.setStatus('required', ageGate.userId)
          }
          if (responseData?.code === 'ANONYMOUS_USER') {
            const requestUrl = error.config?.url || ''
            const isWcPickPath = /^\/competitions\/[^/]+\/picks\/[^/]+/.test(requestUrl)
            if (!isWcPickPath) {
              void useDialogStore.getState().showDialog({
                title: 'Sign up to continue',
                message: 'Create a free account to unlock this feature and keep your game history.',
                confirmText: 'Sign Up',
                cancelText: 'Not now',
              }).then((confirmed) => {
                if (confirmed) {
                  router.replace(buildPublicSignInHref({ signup: true }) as `/${string}`)
                }
              })
            }
          }
        }

        if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
          const requestPath = originalRequest.url || ''
          const isLogoutRequest = requestPath.includes('/auth/logout')

          originalRequest._retry = true

          let freshToken: string | null

          if (refreshingTokenPromiseRef.current) {
            freshToken = await refreshingTokenPromiseRef.current
          } else {
            tokenCacheRef.current = { token: null, timestamp: 0, authIdentity: authIdentityRef.current }

            try {
              refreshingTokenPromiseRef.current = refreshAccessTokenRef.current()
              freshToken = await refreshingTokenPromiseRef.current
            } finally {
              refreshingTokenPromiseRef.current = null
            }

            if (freshToken) {
              tokenCacheRef.current = {
                token: freshToken,
                timestamp: Date.now(),
                authIdentity: authIdentityRef.current,
              }
            }
          }

          if (freshToken) {
            originalRequest.headers = originalRequest.headers || {}
            originalRequest.headers.Authorization = `Bearer ${freshToken}`

            if (isApiDebugEnabled) {
              logger.debug('🔄 [apiClient] Retrying after token refresh:', {
                endpoint: originalRequest.url,
              })
            }

            return axiosInstance(originalRequest)
          }

          if (
            !isLogoutRequest &&
            !authDeathHandledRef.current &&
            isSignedInRef.current &&
            !isAnonymousRef.current
          ) {
            authDeathHandledRef.current = true
            logger.warn('[apiClient] Session refresh failed after 401 — signing out', {
              endpoint: requestPath,
            })
            try {
              await signOutRef.current()
            } catch (signOutError) {
              logger.warn('[apiClient] signOut after auth death failed', {
                message: signOutError instanceof Error ? signOutError.message : String(signOutError),
              })
            }
            router.replace(buildPublicSignInHref() as `/${string}`)
          }
        }

        if (isApiDebugEnabled) {
          const errorMessage =
            error.response?.data && typeof error.response.data === 'object' && 'message' in error.response.data
              ? (error.response.data as { message: string }).message
              : error.message || 'Unknown error'
          logger.error('❌ [apiClient] Error response:', {
            endpoint: originalRequest?.url,
            status: error.response?.status,
            statusText: error.response?.statusText,
            errorMessage,
            fullUrl: originalRequest ? `${API_URL}${originalRequest.url}` : '',
          })
          logApiError(originalRequest?.url || '', error, errorMessage)
        }

        // Retry idempotent requests on a transient network failure (fresh connection) before
        // surfacing it. Only after retries are exhausted do we flip the offline banner, so a
        // single dropped socket no longer shows a false "offline" state (see backoff note above).
        if (isNetworkError(error) && originalRequest) {
          const netRequest = originalRequest as AxiosRequestConfig & { _netRetryCount?: number }
          const attempt = netRequest._netRetryCount ?? 0
          const retryable = isNetworkRetryable(netRequest.method || 'get', netRequest.url || '')
          if (retryable && attempt < NETWORK_RETRY_BACKOFF_MS.length) {
            netRequest._netRetryCount = attempt + 1
            await delay(NETWORK_RETRY_BACKOFF_MS[attempt])
            return axiosInstance(netRequest)
          }
        }

        if (isNetworkError(error)) {
          markNetworkUnreachable()
        }

        return Promise.reject(error)
      }
    )

    return axiosInstance
  }, [TOKEN_CACHE_TTL])

  return useCallback(
    async <T = unknown>(endpoint: string, options?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
      return instance({
        url: endpoint,
        ...options,
      })
    },
    [instance]
  )
}

const baseInstance = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
})

export async function apiClient<T = unknown>(
  endpoint: string,
  getToken: () => Promise<string | null>,
  options?: AxiosRequestConfig
): Promise<AxiosResponse<T>> {
  const token = await getToken()
  return baseInstance({
    url: endpoint,
    ...options,
    headers: {
      ...options?.headers,
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  })
}

export function extractErrorMessage(error: unknown, defaultMessage: string = 'An error occurred'): string {
  if (error && typeof error === 'object' && 'isAxiosError' in error && error.isAxiosError) {
    const axiosError = error as AxiosError
    const responseData = axiosError.response?.data

    if (responseData && typeof responseData === 'object' && 'message' in responseData) {
      const message = (responseData as { message: unknown }).message
      if (typeof message === 'string' && message) {
        return message
      }
    }

    if (responseData && typeof responseData === 'object' && 'error' in responseData) {
      const errorField = (responseData as { error: unknown }).error
      if (typeof errorField === 'string' && errorField) {
        return errorField
      }
    }

    if (axiosError.message) {
      return axiosError.message
    }
  }

  if (error instanceof Error) {
    return error.message || defaultMessage
  }

  return defaultMessage
}

/**
 * True when the error is a 429 rate-limit response that has already been
 * surfaced to the user via the global snackbar. Catch blocks should skip
 * their own error dialogs when this returns true.
 */
export function isRateLimitError(error: unknown): boolean {
  return Boolean(
    error && typeof error === 'object' && 'rateLimitHandled' in error,
  )
}

/**
 * True when the error is a transient 5xx server error that is safe to retry.
 * Covers the first-login race where ensureUserExists returns null briefly while
 * linkExistingUserToSupabaseId runs (e.g. re-provisioned / deleted+recreated users).
 */
export function isTransientServerError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const e = error as { response?: { status?: number }; isAxiosError?: boolean }
  if (!e.isAxiosError) return false
  const status = e.response?.status
  return status !== undefined && status >= 500
}

/**
 * True when the error is a network/unreachable failure (no response from server).
 * Use to log these as warn instead of error (e.g. API not running, offline).
 */
export function isNetworkError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const e = error as { code?: string; response?: unknown; message?: string }
  if (e.response != null) return false
  // Cancellations/aborts are not connectivity failures — never treat them as "offline".
  if (e.code === 'ERR_CANCELED') return false
  if (e.code === 'ERR_NETWORK') return true
  if (typeof e.message === 'string' && e.message === 'Network Error') return true
  if ('isAxiosError' in error && (error as { isAxiosError?: boolean }).isAxiosError) return true
  return false
}
